import React, { useRef, useState } from 'react';
import '../Dpe/DpeUpload.css';
import './QuoteUpload.css';
import {
  Box,
  Button,
  Flex,
  Heading,
  Icon,
  IconButton,
  Link,
  Text,
  useDisclosure,
  Tooltip,
} from '@chakra-ui/core';
import { Formik, useField } from 'formik';
import { useDispatch } from 'react-redux';
import { FileUploader } from 'react-drag-drop-files';
import { PrimaryButton, SecondaryButton } from '../Buttons';
import FileUploadError from '../FileUploadError';
import { MAX_UPLOAD_SIZE_MB, UPLOAD_SIZE_ERROR } from '../../helpers/fileUpload';
import { toastActions } from '../../../redux/toast/actions';
import { TOAST_MESSAGE } from '../Toasts/Toast';
import { quotesAPI } from '../../api/quotes';
import {
  ActionModal,
  ActionModalBody,
  ActionModalCloseButton,
  ActionModalContent,
  ActionModalFooter,
  ActionModalHeader,
  ActionModalOverlay,
} from '../ActionModal';

const QuoteUpload = ({
  folder,
  quoteRequest,
  quote,
  successCallback,
  triggerVariant = 'icon',
  triggerLabel = 'Choisir un fichier',
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isConfirmationOpen,
    onOpen: onConfirmationOpen,
    onClose: onConfirmationClose,
  } = useDisclosure();

  const { isOpen: isSuccessOpen, onOpen: onSuccessOpen, onClose: onSuccessClose } = useDisclosure();

  const dispatch = useDispatch();
  const confirmationSubmitLocked = useRef(false);

  const onModalClose = formik => {
    formik.resetForm();
    onClose();
  };

  const onSuccessModalClose = () => {
    onSuccessClose();
    successCallback();
  };

  const upload = async values => {
    const formData = new FormData();
    formData.append('quoteFile', values.quoteFile, 'quoteFile');

    return quotesAPI.uploadQuoteFile(quoteRequest.id, formData);
  };

  const disallowUpload =
    !quote || quote.folderState === 'Cloturé' || quote.quoteUploadAllowed === false;

  return (
    <>
      {triggerVariant === 'button' ? (
        <Button
          type="button"
          variantColor="neutral"
          size="sm"
          onClick={onOpen}
          bg="coral.900"
          color="white"
          isDisabled={disallowUpload}
          aria-label={triggerLabel}
          _hover={{ bg: 'semiDarkGray.900' }}
          _disabled={{
            bg: 'lightGray3',
            color: 'semiDarkGray.600',
            opacity: 1,
            cursor: 'not-allowed',
          }}
          _focus={{ shadow: 'none' }}
        >
          {triggerLabel}
        </Button>
      ) : (
        <Tooltip
          zIndex="tooltip"
          bg="semiDarkGray.900"
          aria-label="Mise en ligne d'un nouveau devis"
          label="Mise en ligne d'un nouveau devis"
          placement="top"
        >
          <IconButton
            icon="upload"
            variant="ghost"
            variantColor={null}
            onClick={onOpen}
            color="coral.900"
            isDisabled={disallowUpload}
            height={8}
            minW={8}
            rounded="full"
            aria-label="Mise en ligne d'un nouveau devis"
            _hover={{
              '&:not([aria-disabled])': { color: 'white', backgroundColor: 'coral.900' },
            }}
            _disabled={{
              color: 'semiDarkGray.600',
              opacity: 0.5,
              cursor: 'not-allowed',
            }}
            _focus={{ shadow: 'none' }}
          />
        </Tooltip>
      )}

      <SuccessModal
        isOpen={isSuccessOpen}
        onClose={onSuccessModalClose}
        onOpen={onSuccessOpen}
        successCallback={successCallback}
      />

      <Formik
        initialValues={{ quoteFile: null }}
        enableReinitialize
        validateOnChange={false}
        validateOnBlur={false}
        onSubmit={async (values, { setTouched, setErrors, resetForm }) => {
          return upload(values)
            .then(() => {
              setTouched({});
              onModalClose({ resetForm });
              onSuccessOpen();
            })
            .catch(err => {
              dispatch(toastActions.error(TOAST_MESSAGE.error.quoteUpload));
              if (err?.response?.data) {
                if (typeof err?.response?.data.errors === 'object') {
                  setErrors(err?.response?.data.errors);
                } else {
                  setErrors({ quoteFile: err?.response?.data.errors });
                }
              } else {
                console.error(err);
              }
            })
            .finally(() => {
              confirmationSubmitLocked.current = false;
            });
        }}
      >
        {formik => (
          <>
            <ConfirmationModal
              isOpen={isConfirmationOpen}
              onOpen={onConfirmationOpen}
              onClose={onConfirmationClose}
              isSubmitting={formik.isSubmitting}
              onSubmit={() => {
                if (formik.isSubmitting || confirmationSubmitLocked.current) return;

                confirmationSubmitLocked.current = true;
                onConfirmationClose();
                formik.submitForm();
              }}
            />
            <ActionModal isOpen={isOpen} onClose={() => onModalClose(formik)}>
              <ActionModalOverlay data-testid="quote-upload-modal-overlay" />
              <ActionModalContent data-testid="quote-upload-modal">
                <>
                  <ActionModalHeader>
                    <Heading as="h2" fontSize="xl" fontWeight="bold">
                      <Text
                        as="span"
                        fontFamily="heading"
                        opacity="0.5"
                        textTransform="uppercase"
                        fontWeight="600"
                      >
                        Dossier n°{' '}
                      </Text>
                      {folder.formattedId}{' '}
                      <Text as="span" d="inline-block" mx={2} opacity="0.65">
                        -
                      </Text>{' '}
                      <Text mt={2} as="small">
                        {folder.recipientFullName || '-'}
                      </Text>
                    </Heading>
                    <Text fontWeight="bold">
                      {`Entreprise : ${quoteRequest.companyName || '-'}`}
                    </Text>
                    <Text as="small">{quoteRequest.indicator}</Text>
                  </ActionModalHeader>
                  <ActionModalCloseButton />
                  <ActionModalBody>
                    <form
                      id="quoteUploadForm"
                      onSubmit={e => {
                        if (
                          quote.lastQuoteUploaded?.downloadLink ||
                          quote.lastQuoteUploaded?.filename
                        ) {
                          e.preventDefault();
                          onConfirmationOpen();
                        } else {
                          formik.handleSubmit(e);
                        }
                      }}
                    >
                      <QuoteUploadInput
                        id="quoteFile"
                        name="quoteFile"
                        quoteVersion={quote.lastQuoteUploaded}
                      />
                    </form>
                  </ActionModalBody>
                  <ActionModalFooter data-testid="quote-upload-modal-footer">
                    <SecondaryButton type="button" mr={3} onClick={() => onModalClose(formik)}>
                      Annuler
                    </SecondaryButton>
                    <PrimaryButton
                      type="submit"
                      disabled={!formik.values.quoteFile || formik.isSubmitting}
                      isLoading={formik.isSubmitting}
                      loadingText="Envoi en cours"
                      form="quoteUploadForm"
                    >
                      Envoyer
                    </PrimaryButton>
                  </ActionModalFooter>
                </>
              </ActionModalContent>
            </ActionModal>
          </>
        )}
      </Formik>
    </>
  );
};

export default QuoteUpload;

export const QuoteUploadInput = props => {
  const [field, meta, helpers] = useField(props);
  const [fileName, setFileName] = useState('');
  const [previewURL, setPreviewURL] = useState('');

  const handleChange = file => {
    if (!file) return;
    helpers.setTouched(true, false);
    helpers.setError(null);
    setFileName(file.name);
    setPreviewURL(`${URL.createObjectURL(file)}#toolbar=0`);
    helpers.setValue(file);
  };

  const rejectFile = error => {
    helpers.setValue(null, false);
    helpers.setTouched(true, false);
    helpers.setError([error]);
    setFileName('');
    setPreviewURL('');
  };

  return (
    <>
      <QuoteUploadWrapper>
        <Box mb={4}>
          <QuoteUploadFilename fileName={fileName} quoteVersion={props.quoteVersion} />
          {meta.touched && <FileUploadError error={meta.error} />}
        </Box>
        <FileUploader
          name={props.id || field.name}
          types={['PDF']}
          maxSize={MAX_UPLOAD_SIZE_MB}
          handleChange={handleChange}
          onBlur={field.onBlur}
          onSizeError={() => rejectFile(UPLOAD_SIZE_ERROR)}
          onTypeError={() => rejectFile('Le devis doit être un fichier PDF.')}
          uploadedLabel="Fichier sélectionné"
          label="Sélectionner ou glisser-déposer un fichier"
          ariaLabel="Sélectionner ou glisser-déposer un devis PDF"
          classes="dragndrop quote-upload-dropzone"
        >
          <Flex width="100%" alignItems="center" gridGap={3} minWidth={0}>
            <Icon name="upload" color="coral.900" flexShrink={0} />
            <Text fontSize="sm" fontWeight="semibold" lineHeight="short" flex={1} minWidth={0}>
              Sélectionner ou glisser-déposer un fichier
            </Text>
            <Text
              ml="auto"
              color="semiDarkGray.600"
              fontSize="xs"
              whiteSpace="nowrap"
              d={{ base: 'none', sm: 'block' }}
            >
              {`PDF · ${MAX_UPLOAD_SIZE_MB} Mo max`}
            </Text>
          </Flex>
        </FileUploader>
        <Text mt={2} fontSize="xs" color="semiDarkGray.600">
          {MAX_UPLOAD_SIZE_MB} Mo maximum par fichier.
        </Text>
      </QuoteUploadWrapper>
      {previewURL && (
        <Box
          as="embed"
          src={previewURL}
          width="100%"
          height={{ base: '240px', md: '320px' }}
          mt={5}
          border="1px solid"
          borderColor="placeholder"
          rounded="lg"
        />
      )}
    </>
  );
};

export const QuoteUploadWrapper = ({ children, ...props }) => {
  return (
    <Box
      bg="lightGray4"
      border="1px solid"
      borderColor="lightGray3"
      rounded="10px"
      p={{ base: 4, md: 5 }}
      {...props}
    >
      {children}
    </Box>
  );
};

export const QuoteUploadFilename = ({
  fileName,
  placeholder = 'Aucun devis disponible. Déposez un PDF.',
  ...props
}) => {
  return props.quoteVersion?.downloadLink && !fileName ? (
    <Link
      fontWeight={600}
      color="coral.900"
      href={props.quoteVersion?.downloadLink || ''}
      target="_blank"
    >
      {props.quoteVersion.filename || props.quoteVersion.sharepointFilename}
    </Link>
  ) : (
    <Text color={fileName ? null : 'placeholder'}>{fileName || placeholder}</Text>
  );
};

const ConfirmationModal = ({ isOpen, onSubmit, onClose, isSubmitting }) => {
  return (
    <ActionModal isOpen={isOpen} onClose={onClose}>
      <ActionModalOverlay zIndex={2100} />
      <ActionModalContent zIndex={2101} maxWidth="32rem">
        <ActionModalHeader>Confirmation d'envoi de devis</ActionModalHeader>
        <ActionModalCloseButton />
        <ActionModalBody>
          <p>Une nouvelle version du devis va être créée. Confirmez-vous cet envoi ?</p>
        </ActionModalBody>

        <ActionModalFooter>
          <SecondaryButton mr={3} onClick={onClose}>
            Non
          </SecondaryButton>
          <PrimaryButton
            backgroundColor="error"
            disabled={isSubmitting}
            isLoading={isSubmitting}
            loadingText="Envoi en cours"
            onClick={onSubmit}
          >
            Confirmer
          </PrimaryButton>
        </ActionModalFooter>
      </ActionModalContent>
    </ActionModal>
  );
};

const SuccessModal = ({ isOpen, onClose }) => {
  return (
    <ActionModal isOpen={isOpen} onClose={onClose}>
      <ActionModalOverlay />
      <ActionModalContent maxWidth="28rem">
        <ActionModalHeader>Confirmation de l'envoi</ActionModalHeader>
        <ActionModalCloseButton />
        <ActionModalBody>
          <p>Votre devis a bien été envoyé.</p>
        </ActionModalBody>
        <ActionModalFooter>
          <PrimaryButton onClick={onClose}>OK</PrimaryButton>
        </ActionModalFooter>
      </ActionModalContent>
    </ActionModal>
  );
};
